import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, MapPin, Globe, MessageCircle, Loader2 } from "lucide-react";
import FollowButton from "@/components/platform/FollowButton";
import ShareButton from "@/components/platform/ShareButton";
import ProfileShowcase, { parseGallery, parsePricing, parseSocials, SOCIAL_FIELDS } from "@/components/platform/ProfileShowcase";
import { getProfileBanner, showcaseGallery } from "@/lib/profileBanner";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import Footer from "@/components/Footer";
import MediaImage from "@/components/platform/MediaImage";
import { useMediaUrl } from "@/lib/storageUrl";

/** Public-facing member page — Groovers, Creators and Venues all get one at /u/:username. */
const PublicProfile = () => {
  const { username = "" } = useParams();
  const { userId, loading: authLoading } = useCurrentUser();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["public-profile", username],
    enabled: !!username,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("public-profile", { body: { username } });
      if (error) throw error;
      return data?.profile;
    },
  });
  const avatarUrl = useMediaUrl(profile?.avatar_url);
  useEffect(() => {
    if (profile) document.title = `${profile.full_name || profile.username} | Metro City Groove`;
  }, [profile]);

  if (authLoading || isLoading) {
    return <div className="min-h-screen bg-black text-white grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-black text-white grid place-items-center px-6 text-center">
        <div>
          <h1 className="text-2xl font-bold">Profile not found</h1>
          <p className="text-gray-400 mt-2">No member on Metro City Groove uses @{username}.</p>
          <Link to="/"><Button className="mt-5">Back home</Button></Link>
        </div>
      </div>
    );
  }

  const socials = parseSocials(profile.social_links);
  const allGallery = parseGallery(profile.gallery);
  const gallery = showcaseGallery(allGallery);
  const bannerUrl = getProfileBanner(allGallery);
  const pricing = parsePricing(profile.pricing);
  const kind = profile.user_type === "venue" ? "venue" : "creator";
  const name = profile.full_name || profile.username;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Metro City Groove
          </Link>
          <ShareButton path={`/u/${profile.username}`} title={`${name} on Metro City Groove`} />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-4xl space-y-8">
        <div className="relative h-32 sm:h-48 bg-secondary overflow-hidden rounded-md">
          {bannerUrl && <MediaImage src={bannerUrl} alt={`${name} banner`} className="h-full w-full object-cover" />}
        </div>
        <section className="flex flex-col sm:flex-row gap-5 sm:items-center">
          <Avatar className="w-24 h-24 border border-white/10">
            <AvatarImage src={avatarUrl ?? undefined} alt={`${name} profile picture`} />
            <AvatarFallback className="text-2xl">{name?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold truncate">{name}</h1>
              <Badge variant="outline" className="border-gray-700 text-gray-300 capitalize">{profile.user_type ?? "groover"}</Badge>
              {profile.verification_status === "verified" && <Badge className="bg-blue-500/15 text-blue-300 border-0">Verified</Badge>}
              {profile.available_for_work && <Badge className="bg-emerald-500/15 text-emerald-300 border-0">Available to work</Badge>}
            </div>
            <p className="text-sm text-gray-500">@{profile.username}</p>
            {(profile.city || profile.country) && (
              <p className="text-sm text-gray-400 flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />{[profile.city, profile.country].filter(Boolean).join(", ")}
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <FollowButton targetId={profile.id} size="default" />
              <Link to={`/messages?to=${profile.id}`}>
                <Button variant="outline"><MessageCircle className="w-4 h-4 mr-1.5" />Message</Button>
              </Link>
            </div>
          </div>
        </section>

        {profile.bio && <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{profile.bio}</p>}
        {profile.availability_note && <p className="text-sm text-emerald-300/80">{profile.availability_note}</p>}

        {(profile.website || Object.keys(socials).length > 0) && (
          <div className="flex flex-wrap gap-2">
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noreferrer noopener">
                <Button size="sm" variant="outline"><Globe className="w-4 h-4 mr-1.5" />Website</Button>
              </a>
            )}
            {SOCIAL_FIELDS.filter((f) => socials[f.key]).map((f) => (
              <a key={f.key} href={socials[f.key]} target="_blank" rel="noreferrer noopener">
                <Button size="sm" variant="outline"><f.icon className="w-4 h-4 mr-1.5" />{f.label}</Button>
              </a>
            ))}
          </div>
        )}

        <ProfileShowcase
          isOwn={false}
          kind={kind}
          website={profile.website}
          socials={socials}
          gallery={gallery}
          pricing={pricing}
        />
      </main>
      <Footer />
    </div>
  );
};

export default PublicProfile;
